import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Loader2, MoreVertical, Trash2, ExternalLink, Globe, LogOut, Pencil, MonitorSmartphone, Zap, Sparkles, Receipt } from "lucide-react";
import { Logo } from "@/components/Logo";

function Header({ user, onLogout, onLogoutAll, onUpgrade, onBilling }) {
  const isPro = user?.plan === "pro";
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-bold text-lg">
          <Logo />
          Angiò A.I.
        </div>
        <div className="flex items-center gap-3">
          <Link to="/vetrina">
            <Button variant="ghost" className="h-8 px-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5" data-testid="dashboard-vetrina-btn">
              Vetrina
            </Button>
          </Link>
          {isPro ? (
            <span className="flex items-center gap-1.5 text-xs font-mono text-primary border border-primary/40 bg-primary/10 rounded-full px-2.5 py-1" data-testid="pro-badge">
              <Zap className="w-3.5 h-3.5" /> PRO
            </span>
          ) : (
            <Button onClick={onUpgrade} className="h-8 px-3 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 font-semibold text-xs" data-testid="header-upgrade-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Passa a Pro
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
                data-testid="user-menu-trigger"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold">
                  {(user?.name || "U")[0].toUpperCase()}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141414] border-white/10 text-white">
              <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
              <DropdownMenuItem onClick={onUpgrade} className="cursor-pointer" data-testid="menu-billing-btn">
                <Zap className="w-4 h-4 mr-2" /> {isPro ? "Gestisci abbonamento" : "Piani e abbonamento"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onBilling} className="cursor-pointer" data-testid="menu-invoices-btn">
                <Receipt className="w-4 h-4 mr-2" /> Fatturazione
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} className="cursor-pointer" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogoutAll} className="cursor-pointer" data-testid="logout-all-btn">
                <MonitorSmartphone className="w-4 h-4 mr-2" /> Esci da tutti i dispositivi
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default function Dashboard() {
  const { user, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createProject = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { title: "Nuovo sito" });
      navigate(`/builder/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        navigate("/upgrade");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Progetto eliminato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg text-white">
      <Header user={user} onLogout={logout} onLogoutAll={logoutAll} onUpgrade={() => navigate("/upgrade")} onBilling={() => navigate("/billing")} />

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-bold">I tuoi siti</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Crea, modifica e pubblica i tuoi siti web con l'AI.
            </p>
          </div>
          <Button
            onClick={createProject}
            disabled={creating}
            className="bg-primary text-black hover:bg-[#33F3FF] font-semibold h-11 px-5 transition-transform hover:-translate-y-0.5"
            data-testid="create-project-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Crea un sito</>}
          </Button>
        </div>

        {projects === null ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : projects.length === 0 ? (
          <div
            className="border border-dashed border-white/15 rounded-2xl py-24 flex flex-col items-center text-center"
            data-testid="empty-state"
          >
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-5">
              <Globe className="w-7 h-7" />
            </div>
            <h3 className="font-display font-semibold text-xl">Nessun sito ancora</h3>
            <p className="text-muted-foreground text-sm mt-2 max-w-sm">
              Inizia creando il tuo primo sito. Basta descriverlo a parole.
            </p>
            <Button
              onClick={createProject}
              disabled={creating}
              className="mt-6 bg-primary text-black hover:bg-[#33F3FF] font-semibold"
              data-testid="empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Crea un sito
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group border border-white/10 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all overflow-hidden"
                data-testid={`project-card-${p.id}`}
              >
                <div
                  className="h-36 bg-[#101010] border-b border-white/10 flex items-center justify-center cursor-pointer relative"
                  onClick={() => navigate(`/builder/${p.id}`)}
                >
                  <Globe className="w-8 h-8 text-white/15" />
                  {p.published && (
                    <span className="absolute top-3 left-3 text-[10px] font-mono text-success border border-success/30 bg-success/10 rounded-full px-2 py-0.5">
                      PUBBLICATO
                    </span>
                  )}
                </div>
                <div className="p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/builder/${p.id}`)}>
                    <h3 className="font-display font-semibold truncate">{p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(p.updated_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-muted-foreground hover:text-white p-1" data-testid={`project-menu-${p.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#141414] border-white/10 text-white">
                      <DropdownMenuItem onClick={() => navigate(`/builder/${p.id}`)} className="cursor-pointer">
                        <Pencil className="w-4 h-4 mr-2" /> Apri
                      </DropdownMenuItem>
                      {p.published && p.slug && (
                        <DropdownMenuItem asChild className="cursor-pointer">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/s/${p.slug}`} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" /> Vedi online
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => setDeleteId(p.id)}
                        className="cursor-pointer text-destructive focus:text-destructive"
                        data-testid={`delete-project-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Elimina
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il progetto?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Questa azione è irreversibile. Il sito verrà eliminato definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
